import React, {Fragment} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Alert,
    Button,
    Stack,
    Text,
    Box,
    IconButton,
    Flex,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AlertIcon} from '@salesforce/retail-react-app/app/components/icons'
import {CustomClose} from '../../components/icons'
import ResetPasswordFields from '../../components/forms/reset-password-fields'
import ResetPasswordLanding from '../../pages/reset-password/reset-password-landing'
import {useHistory} from 'react-router-dom'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'

const ResetPasswordForm = ({
    submitForm,
    form,
    isAccountDashboard = false,
    setShouldShowHeading = noop,
    clickSignIn = noop
}) => {
    const history = useHistory()
    const navigate = useNavigation()
    const isMobile = useBreakpointValue({base: true, lg: false})

    return (
        <Fragment>
            {!isAccountDashboard && (
                <Box position="relative">
                    <IconButton
                        icon={<CustomClose />}
                        aria-label="Close"
                        position="absolute"
                        top="19px"
                        right="-15px"
                        variant="ghost"
                        display={{base: 'flex', lg: 'none'}}
                        onClick={() => history.push('/login')}
                    />
                </Box>
            )}

            {!form.formState.isSubmitSuccessful ? (
                <form
                    onSubmit={form.handleSubmit(submitForm)}
                    data-testid="sf-auth-modal-form"
                    noValidate
                >
                    {isMobile ? (
                        <Flex direction="column" minH="100vh" pb="calc(60px + 72px)">
                            <Stack spacing={4} mt={isAccountDashboard ? 0 : '77px'} flex="1">
                                {isAccountDashboard ? (
                                    <Text
                                        fontSize="md"
                                        fontWeight="bold"
                                        lineHeight="20px"
                                        textTransform="uppercase"
                                    >
                                        <FormattedMessage
                                            defaultMessage="Change Password"
                                            id="account_detail.title.change_password"
                                        />
                                    </Text>
                                ) : (
                                    <Text
                                        fontSize="md"
                                        fontWeight="bold"
                                        lineHeight="20px"
                                        textTransform="uppercase"
                                    >
                                        <FormattedMessage
                                            defaultMessage="Forgot Your Password?"
                                            id="reset_password_form.title.forgot_password"
                                        />
                                    </Text>
                                )}

                                <Text fontSize="sm" lineHeight="18px">
                                    {isAccountDashboard ? (
                                        <FormattedMessage
                                            defaultMessage="Enter your email and we will send you more information to change your password."
                                            id="change_password_form.info.enter_your_email"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            defaultMessage="Enter your email and we will send you more information to reset your password."
                                            id="reset_password_form.info.enter_your_email"
                                        />
                                    )}
                                </Text>

                                {form.formState.errors?.global && (
                                    <Alert status="error">
                                        <AlertIcon color="red.500" boxSize={4} />
                                        <Text fontSize="sm" ml={3}>
                                            {form.formState.errors.global.message}
                                        </Text>
                                    </Alert>
                                )}

                                <ResetPasswordFields
                                    form={form}
                                    isAccountDashboard={isAccountDashboard}
                                />
                            </Stack>

                            <Box position="sticky" bottom="60px" mt="auto" bg="white" zIndex="1000">
                                <Button
                                    type="submit"
                                    onClick={() => form.clearErrors('global')}
                                    isLoading={form.formState.isSubmitting}
                                    isDisabled={!form.watch('email')}
                                    width="100%"
                                    fontSize="md"
                                    lineHeight="20px"
                                >
                                    <FormattedMessage
                                        defaultMessage="Send email"
                                        id="reset_password_form.button.send_email"
                                    />
                                </Button>
                            </Box>
                        </Flex>
                    ) : (
                        <Stack spacing={6} mt={isAccountDashboard ? 0 : '98px'} h="218px" w="459px">
                            {isAccountDashboard ? (
                                <Text
                                    fontSize="md"
                                    lineHeight="20px"
                                    fontWeight="bold"
                                    textTransform="uppercase"
                                >
                                    <FormattedMessage
                                        defaultMessage="Change Password"
                                        id="account_detail.title.change_password"
                                    />
                                </Text>
                            ) : (
                                <Text
                                    fontSize="md"
                                    lineHeight="20px"
                                    fontWeight="bold"
                                    textTransform="uppercase"
                                >
                                    <FormattedMessage
                                        defaultMessage="Forgot Your Password?"
                                        id="reset_password_form.title.forgot_password"
                                    />
                                </Text>
                            )}

                            <Text fontSize="sm" lineHeight="18px">
                                {isAccountDashboard ? (
                                    <FormattedMessage
                                        defaultMessage="Enter your email and we will send you more information to change your password."
                                        id="change_password_form.info.enter_your_email"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Enter your email and we will send you more information to reset your password."
                                        id="reset_password_form.info.enter_your_email"
                                    />
                                )}
                            </Text>

                            {form.formState.errors?.global && (
                                <Alert status="error">
                                    <AlertIcon color="red.500" boxSize={4} />
                                    <Text fontSize="sm" ml={3}>
                                        {form.formState.errors.global.message}
                                    </Text>
                                </Alert>
                            )}

                            <ResetPasswordFields
                                isAccountDashboard={isAccountDashboard}
                                form={form}
                            />

                            <Button
                                type="submit"
                                onClick={() => form.clearErrors('global')}
                                isLoading={form.formState.isSubmitting}
                                isDisabled={!form.watch('email')}
                                width="100%"
                                fontSize="md"
                                lineHeight="20px"
                            >
                                <FormattedMessage
                                    defaultMessage="Send email"
                                    id="reset_password_form.button.send_email"
                                />
                            </Button>
                        </Stack>
                    )}
                </form>
            ) : (
                <Stack
                    justify="center"
                    spacing={{base: '41px', lg: '94px'}}
                    pt={isAccountDashboard ? 0 : {base: '77px', lg: '98px'}}
                >
                    <ResetPasswordLanding
                        email={form.getValues('email')}
                        submitForm={submitForm}
                        clickSignIn={isAccountDashboard ? clickSignIn : () => navigate('/login')}
                        setShouldShowHeading={setShouldShowHeading}
                    />
                </Stack>
            )}
        </Fragment>
    )
}

ResetPasswordForm.propTypes = {
    submitForm: PropTypes.func,
    setShouldShowHeading: PropTypes.func,
    form: PropTypes.object,
    isAccountDashboard: PropTypes.bool,
    clickSignIn: PropTypes.func
}

export default ResetPasswordForm
